import type {
  ApiResponse,
  PaginatedResponse,
  Link,
  User,
  AnalyticsSummary,
  QRConfig,
  ApiKey,
} from "@ooo-codes/shared";

const BASE_URL =
  window.location.hostname === "localhost" ? "/api" : "https://ooo.codes/api";

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem("token");

  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Only set Content-Type for non-FormData bodies
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>),
    },
  });

  const data = await res.json();

  if (!res.ok) {
    throw new ApiError(
      (data as ApiResponse).error ?? "Request failed",
      res.status
    );
  }

  return data as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Public Usage (no auth) ───

export interface UsageData {
  requests_today: number;
  daily_limit: number;
}

export const usage = {
  get: () => request<ApiResponse<UsageData>>("/usage"),
};

// ─── Auth ───

export const auth = {
  registerOptions: (body: {
    username: string;
    display_name: string;
    invitation_token?: string;
  }) =>
    request<ApiResponse>("/auth/register/options", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  registerVerify: (body: unknown) =>
    request<ApiResponse<{ user: unknown; token: string; pending_approval?: boolean }>>(
      "/auth/register/verify",
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),

  loginOptions: (body?: { username?: string }) =>
    request<ApiResponse>("/auth/login/options", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),

  loginVerify: (body: unknown) =>
    request<ApiResponse<{ user: unknown; token: string }>>(
      "/auth/login/verify",
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),

  me: () => request<ApiResponse>("/auth/me"),

  setup: () => request<ApiResponse<{ needs_setup: boolean }>>("/auth/setup"),
};

// ─── Links ───

export const links = {
  list: (page = 1, perPage = 20, search?: string) => {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
    });
    if (search) params.set("search", search);
    return request<PaginatedResponse<Link>>(`/links?${params}`);
  },

  get: (id: string) => request<ApiResponse<Link>>(`/links/${id}`),

  create: (body: {
    url: string;
    slug?: string;
    title?: string;
    expires_at?: string;
  }) =>
    request<ApiResponse<Link>>("/links", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  update: (id: string, body: Record<string, unknown>) =>
    request<ApiResponse<Link>>(`/links/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  delete: (id: string) =>
    request<ApiResponse>(`/links/${id}`, { method: "DELETE" }),

  checkSlug: (slug: string) =>
    request<ApiResponse<{ available: boolean }>>(`/links/check-slug/${slug}`),
};

// ─── Analytics ───

export interface DashboardData {
  total_links: number;
  total_clicks: number;
  clicks_today: number;
  top_links: { id: string; slug: string; title: string | null; click_count: number }[];
  usage: {
    links_created: number;
    clicks_today: number;
    clicks_this_month: number;
  };
}

export const analytics = {
  dashboard: () =>
    request<ApiResponse<DashboardData>>("/analytics/dashboard"),

  get: (linkId: string, days = 30) =>
    request<ApiResponse<AnalyticsSummary>>(
      `/analytics/${linkId}?days=${days}`
    ),
};

// ─── QR ───

export const qr = {
  get: (linkId: string) =>
    request<ApiResponse<QRConfig>>(`/qr/${linkId}`),

  update: (linkId: string, body: Record<string, unknown>) =>
    request<ApiResponse<QRConfig>>(`/qr/${linkId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

// ─── Invitations ───

export interface InvitationItem {
  id: string;
  email: string;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  invite_url: string;
  is_expired: boolean;
  is_accepted: boolean;
}

export const invitations = {
  create: (email: string) =>
    request<ApiResponse<{ invite_url: string }>>("/invitations", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  validate: (token: string) =>
    request<ApiResponse<{ email: string; expires_at: string }>>(
      `/invitations/validate/${token}`
    ),

  list: () =>
    request<ApiResponse<InvitationItem[]>>("/invitations"),

  revoke: (id: string) =>
    request<ApiResponse>(`/invitations/${id}`, { method: "DELETE" }),
};

// ─── API Keys ───

export const apiKeys = {
  list: () => request<ApiResponse<ApiKey[]>>("/api-keys"),

  create: (name: string, expires_at?: string) =>
    request<ApiResponse<ApiKey & { key: string }>>("/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, expires_at }),
    }),

  revoke: (id: string) =>
    request<ApiResponse>(`/api-keys/${id}`, { method: "DELETE" }),
};

// ─── Admin ───

export interface AdminStats {
  total_users: number;
  active_users: number;
  pending_users: number;
  total_links: number;
  total_clicks: number;
  today_signups: number;
}

export type UserDetail = User & { link_count: number; total_clicks: number };

export const admin = {
  stats: () =>
    request<ApiResponse<AdminStats>>("/admin/stats"),

  users: (page = 1, limit = 20, status?: string) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.set("status", status);
    return request<PaginatedResponse<User>>(`/admin/users?${params}`);
  },

  userDetail: (id: string) =>
    request<ApiResponse<UserDetail>>(`/admin/users/${id}`),

  userLinks: (id: string, page = 1, limit = 20) =>
    request<PaginatedResponse<Link>>(`/admin/users/${id}/links?page=${page}&limit=${limit}`),

  updateStatus: (id: string, status: string, reason?: string) =>
    request<ApiResponse<User>>(`/admin/users/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, reason }),
    }),

  updateRole: (id: string, role: "owner" | "member") =>
    request<ApiResponse<User>>(`/admin/users/${id}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),

  updateLimit: (id: string, daily_link_limit: number | null) =>
    request<ApiResponse<User>>(`/admin/users/${id}/limit`, {
      method: "PATCH",
      body: JSON.stringify({ daily_link_limit }),
    }),
};
